import {
  IPartnerConfigurationRow,
  IPartnerConfigurationSnapshotRow,
  IPartnerConfigurationValues,
  PARTNER_CONFIGURATION_ALL,
  PARTNER_CONFIGURATION_DEFAULTS,
  PartnerConfigurationChange,
} from "../../interfaces";

export interface DraftEntry {
  partitionKey: string;
  rowKey: string;
  /** Null marks the row for removal. */
  values: IPartnerConfigurationValues | null;
  /** The row version the edit started from; null when adding a row that didn't exist. */
  etag: string | null;
}
export type Drafts = Record<string, DraftEntry>;

export type RowState = "clean" | "new" | "edited" | "removed";

export interface MergedRow {
  key: string;
  partitionKey: string;
  rowKey: string;
  server: IPartnerConfigurationRow | null;
  values: IPartnerConfigurationValues;
  state: RowState;
  /** The row changed in the table after this draft was started. */
  stale: boolean;
}

export interface PendingChange {
  key: string;
  change: PartnerConfigurationChange;
  before: IPartnerConfigurationValues | null;
  after: IPartnerConfigurationValues | null;
  stale: boolean;
}

export const rowKeyOf = (partitionKey: string, rowKey: string) => `${partitionKey}|${rowKey}`;

export const FIELDS: Array<{ key: keyof IPartnerConfigurationValues; label: string; hint: string }> = [
  { key: "allowCodelessDeals", label: "Codeless deals", hint: "Whether domain search may return a deal when the merchant has no coupon in this country." },
  { key: "codelessDealsAllowedPercentage", label: "Codeless %", hint: "Share of requests that may get a codeless deal. Blank is unthrottled; 0 never, 100 always." },
  { key: "couponEnforcementMode", label: "Coupon enforcement", hint: "Which coupons are surfaced and auto-applied." },
  { key: "offerDisplayMode", label: "Offer display", hint: "When offers are shown alongside coupons." },
  { key: "maxCoupons", label: "Max coupons", hint: "Cap on coupons surfaced. Blank is no cap." },
  { key: "maxAutoCoupons", label: "Max auto coupons", hint: "Cap on coupons tagged for auto-apply. Blank tags every eligible one." },
  { key: "maxOffers", label: "Max offers", hint: "Cap on offers surfaced. Blank is no cap." },
  { key: "currentStanddownRulesVersion", label: "Stand-down version", hint: "Stand-down rules version the client downloads." },
  { key: "currentWhitelistDomainsVersion", label: "Whitelist version", hint: "Whitelisted-domains version the client downloads. Normally auto-generated; only change it if that list exists." },
];

export const pickValues = (v: IPartnerConfigurationValues): IPartnerConfigurationValues => ({
  allowCodelessDeals: v.allowCodelessDeals,
  codelessDealsAllowedPercentage: v.codelessDealsAllowedPercentage,
  couponEnforcementMode: v.couponEnforcementMode,
  offerDisplayMode: v.offerDisplayMode,
  maxCoupons: v.maxCoupons,
  maxAutoCoupons: v.maxAutoCoupons,
  maxOffers: v.maxOffers,
  currentStanddownRulesVersion: v.currentStanddownRulesVersion,
  currentWhitelistDomainsVersion: v.currentWhitelistDomainsVersion,
});

export const sameValues = (a: IPartnerConfigurationValues, b: IPartnerConfigurationValues) =>
  FIELDS.every((f) => a[f.key] === b[f.key]);

export const formatValue = (key: keyof IPartnerConfigurationValues, v: unknown): string => {
  if (v === null || v === undefined || v === "") {
    if (key === "couponEnforcementMode" || key === "offerDisplayMode") return "Platform default";
    return key === "codelessDealsAllowedPercentage" ? "Unthrottled" : "No cap";
  }
  if (typeof v === "boolean") return v ? "On" : "Off";
  return key === "codelessDealsAllowedPercentage" ? `${v}%` : String(v);
};

/** ALL first, then country codes alphabetically. */
export const compareRowKeys = (a: string, b: string) =>
  a === PARTNER_CONFIGURATION_ALL ? -1 : b === PARTNER_CONFIGURATION_ALL ? 1 : a.localeCompare(b);

export const mergeRows = (rows: IPartnerConfigurationRow[], drafts: Drafts, partitionKey: string): MergedRow[] => {
  const merged = new Map<string, MergedRow>();
  for (const r of rows) {
    if (r.partitionKey !== partitionKey) continue;
    const key = rowKeyOf(r.partitionKey, r.rowKey);
    merged.set(key, { key, partitionKey: r.partitionKey, rowKey: r.rowKey, server: r, values: pickValues(r), state: "clean", stale: false });
  }
  for (const [key, d] of Object.entries(drafts)) {
    if (d.partitionKey !== partitionKey) continue;
    const existing = merged.get(key);
    const stale = d.etag !== (existing?.server?.etag ?? null);
    if (existing) {
      existing.stale = stale;
      if (d.values === null) existing.state = "removed";
      else if (!sameValues(d.values, existing.values) || stale) {
        existing.values = d.values;
        existing.state = "edited";
      }
    } else if (d.values) {
      merged.set(key, { key, partitionKey: d.partitionKey, rowKey: d.rowKey, server: null, values: d.values, state: "new", stale });
    }
  }
  return Array.from(merged.values()).sort((a, b) => compareRowKeys(a.rowKey, b.rowKey));
};

/**
 * Drafts turned into table writes. Each write carries the etag its draft started from, so a row
 * changed by someone else since then is rejected rather than overwritten.
 */
export const pendingChanges = (rows: IPartnerConfigurationRow[], drafts: Drafts): PendingChange[] => {
  const byKey = new Map(rows.map((r) => [rowKeyOf(r.partitionKey, r.rowKey), r]));
  const out: PendingChange[] = [];
  for (const [key, d] of Object.entries(drafts)) {
    const server = byKey.get(key);
    const base = { partitionKey: d.partitionKey, rowKey: d.rowKey };
    const before = server ? pickValues(server) : null;
    const stale = d.etag !== (server?.etag ?? null);
    if (d.etag === null) {
      if (d.values) out.push({ key, change: { op: "create", ...base, values: d.values }, before, after: d.values, stale });
    } else if (d.values === null) {
      if (server) out.push({ key, change: { op: "delete", ...base, etag: d.etag }, before, after: null, stale });
    } else if (stale || !before || !sameValues(d.values, before)) {
      out.push({ key, change: { op: "update", ...base, etag: d.etag, values: d.values }, before, after: d.values, stale });
    }
  }
  return out.sort((a, b) => a.change.partitionKey.localeCompare(b.change.partitionKey) || compareRowKeys(a.change.rowKey, b.change.rowKey));
};

/**
 * Starting values for a new row: the partner's ALL row, else the global default row, else
 * Platform defaults carrying the versions the partner's other rows already use.
 */
export const templateValues = (
  rows: IPartnerConfigurationRow[],
  drafts: Drafts,
  partitionKey: string
): { values: IPartnerConfigurationValues; source: string } => {
  const own = mergeRows(rows, drafts, partitionKey).filter((r) => r.state !== "removed");
  const ownAll = own.find((r) => r.rowKey === PARTNER_CONFIGURATION_ALL);
  if (ownAll) return { values: ownAll.values, source: "this partner's ALL row" };
  if (partitionKey !== PARTNER_CONFIGURATION_ALL) {
    const globalAll = mergeRows(rows, drafts, PARTNER_CONFIGURATION_ALL).find((r) => r.rowKey === PARTNER_CONFIGURATION_ALL && r.state !== "removed");
    if (globalAll) return { values: globalAll.values, source: "the all-partners default row" };
  }
  const sample = own[0];
  return {
    values: {
      ...PARTNER_CONFIGURATION_DEFAULTS,
      currentStanddownRulesVersion: sample?.values.currentStanddownRulesVersion ?? PARTNER_CONFIGURATION_DEFAULTS.currentStanddownRulesVersion,
      currentWhitelistDomainsVersion: sample?.values.currentWhitelistDomainsVersion ?? PARTNER_CONFIGURATION_DEFAULTS.currentWhitelistDomainsVersion,
    },
    source: sample ? "Platform defaults, with versions from this partner's other rows" : "Platform defaults",
  };
};

const regionNames = typeof Intl !== "undefined" && "DisplayNames" in Intl ? new Intl.DisplayNames(["en"], { type: "region" }) : null;

export const countryName = (code: string): string => {
  if (code === PARTNER_CONFIGURATION_ALL) return "All countries";
  try {
    return regionNames?.of(code === "UK" ? "GB" : code) ?? code;
  } catch {
    return code;
  }
};

const CSV_COLUMNS: Array<[string, string, keyof IPartnerConfigurationValues]> = [
  ["AllowCodelessDeals", "Boolean", "allowCodelessDeals"],
  ["CodelessDealsAllowedPercentage", "Int32", "codelessDealsAllowedPercentage"],
  ["CouponEnforcementMode", "String", "couponEnforcementMode"],
  ["CurrentStanddownRulesVersion", "Int32", "currentStanddownRulesVersion"],
  ["CurrentWhitelistDomainsVersion", "Int32", "currentWhitelistDomainsVersion"],
  ["MaxAutoCoupons", "Int32", "maxAutoCoupons"],
  ["MaxCoupons", "Int32", "maxCoupons"],
  ["MaxOffers", "Int32", "maxOffers"],
  ["OfferDisplayMode", "String", "offerDisplayMode"],
];

/** Same layout as an Azure Storage Explorer export, so it can be re-imported as a fallback. */
export const toStorageExplorerCsv = (rows: IPartnerConfigurationSnapshotRow[]): string => {
  const header = ["PartitionKey", "RowKey", ...CSV_COLUMNS.flatMap(([name]) => [name, `${name}@type`])];
  const lines = rows.map((r) => [
    r.partitionKey,
    r.rowKey,
    ...CSV_COLUMNS.flatMap(([, type, key]) => {
      const v = r[key];
      return v === null || v === "" ? ["", ""] : [String(v), type];
    }),
  ]);
  return [header, ...lines].map((l) => l.join(",")).join("\n");
};
